import {Card, ConfigProvider, DatePicker, Tag} from 'antd';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import {useEffect, useRef, useState} from 'react';
import {CgFileDocument} from 'react-icons/cg';
import {CiCreditCard1} from 'react-icons/ci';
import {FaLayerGroup} from 'react-icons/fa';
import {FaSackDollar} from 'react-icons/fa6';
import {HiOutlineCash} from 'react-icons/hi';
import {MdPendingActions} from 'react-icons/md';
import {RiMoneyRupeeCircleLine} from 'react-icons/ri';
import {useSelector} from 'react-redux';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {RQGetDashboardBills, RQGetMonthlySales} from '../../../../reactQuery/apis';
import {selectAuth} from '../../../../redux/selectors';

const {RangePicker} = DatePicker;

// ── Animated counter ───────────────────────────────────────────────────────
const AnimatedNumber = ({value, prefix = '', decimals = 0}) => {
  const [display, setDisplay] = useState(0);
  const raf = useRef(null);

  useEffect(() => {
    const start = performance.now();
    const to    = Number(value) || 0;
    const tick  = now => {
      const t = Math.min((now - start) / 900, 1);
      setDisplay(to * (1 - Math.pow(1 - t, 3)));
      if (t < 1) {raf.current = requestAnimationFrame(tick);}
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [value]);

  return (
    <span>
      {prefix}
      {display.toLocaleString('en-IN', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })}
    </span>
  );
};

// ── Shimmer skeleton ───────────────────────────────────────────────────────
const Shimmer = ({className = ''}) => (
  <div className={`animate-pulse bg-primary-200 rounded-lg ${className}`} />
);

// ── Top products list ──────────────────────────────────────────────────────
const TopProducts = ({products = []}) => {
  if (!products.length) {
    return <p className={'text-center text-primary-800 text-sm py-6'}>No product data</p>;
  }
  const max        = Math.max(...products.map(p => p.quantity), 1);
  const rankColors = [
    'text-yellow-400', 'text-primary-400', 'text-amber-600',
    'text-primary-600', 'text-primary-600', 'text-primary-600',
  ];

  return (
    <div className={'flex flex-col gap-3.5'}>
      {products.slice(0, 6).map((p, i) => (
        <div key={i} className={'flex items-center gap-3'}>
          <span className={`font-mono text-xs font-bold w-5 shrink-0 ${rankColors[i] ?? 'text-primary-600'}`}>
            #{i + 1}
          </span>
          <span className={'text-sm text-primary-700 w-36 shrink-0 truncate'}>{p.productName}</span>
          <div className={'flex-1 md:flex hidden h-1.5 overflow-hidden'}>
            <div
              className={'h-full rounded-full bg-gradient-to-r from-cyan-400 to-indigo-500 transition-all duration-700'}
              style={{width: `${Math.round((p.quantity / max) * 100)}%`}}
            />
          </div>
          <span className={'font-mono text-xs text-emerald-700 w-16 text-right shrink-0'}>
            {p.quantity} sold
          </span>
        </div>
      ))}
    </div>
  );
};

// ── Payment donut ──────────────────────────────────────────────────────────
const PaymentDonut = ({cash = 0, upi = 0, card = 0, credit = 0}) => {
  const total = cash + upi + card + credit || 1;
  const pct   = v => Math.round((v / total) * 100);

  const segments = [
    {label: 'Cash',   value: cash,   pct: pct(cash),   color: '#191970', dot: 'bg-blue-900'},
    {label: 'UPI',    value: upi,    pct: pct(upi),    color: '#1F51FF', dot: 'bg-blue-600'},
    {label: 'Card',   value: card,   pct: pct(card),   color: '#1591EA', dot: 'bg-sky-500'},
    {label: 'Credit', value: credit, pct: pct(credit), color: '#7c3aed', dot: 'bg-violet-600'},
  ];

  let acc = 0;
  const stops = segments
    .map(s => { const f = acc; acc += s.pct; return `${s.color} ${f}% ${acc}%`; })
    .join(', ');

  return (
    <div className={'flex items-center gap-8 flex-wrap'}>
      <div
        className={'w-32 h-32 rounded-full shrink-0 relative'}
        style={{background: `conic-gradient(${stops})`}}
      >
        <div className={'absolute inset-5 rounded-full flex flex-col items-center justify-center'}>
          <span className={'text-[10px] font-semibold tracking-widest text-white uppercase leading-tight'}>Sales</span>
          <span className={'text-[10px] font-semibold tracking-widest text-white uppercase leading-tight'}>Split</span>
        </div>
      </div>
      <div className={'flex flex-col gap-3 flex-1'}>
        {segments.map(s => (
          <div key={s.label} className={'flex items-center gap-2'}>
            <span className={`w-3 h-3 rounded-full shrink-0 ${s.dot}`} />
            <span className={'text-primary-600 flex-1'}>{s.label}</span>
            <span className={'font-mono text-slate-200 w-8 text-right'}>{s.pct}%</span>
            <span className={'font-mono text-primary-600 w-24 text-right'}>
              ₹{s.value.toLocaleString('en-IN')}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Bill status row ────────────────────────────────────────────────────────
const BillStatusRow = ({stats, loading}) => {
  const items = [
    {label: 'Paid',      value: stats?.paidBills,      color: 'text-emerald-600', bg: 'bg-emerald-500/10'},
    {label: 'Partial',   value: stats?.partialBills,   color: 'text-amber-700',   bg: 'bg-amber-500/10'},
    {label: 'Pending',   value: stats?.pendingBills,   color: 'text-orange-600',  bg: 'bg-orange-500/10'},
    {label: 'Cancelled', value: stats?.cancelledBills, color: 'text-red-600',     bg: 'bg-red-500/10'},
  ];

  return (
    <div className={'grid grid-cols-2 sm:grid-cols-4 gap-3'}>
      {items.map(item => (
        <div
          key={item.label}
          className={`rounded-md px-4 py-3 flex flex-col items-center gap-1 ${item.bg}`}
        >
          <span className={'text-xs font-bold tracking-widest uppercase text-primary-700'}>
            {item.label}
          </span>
          <span className={`text-2xl font-bold font-mono ${item.color}`}>
            {loading
              ? <Shimmer className={'h-7 w-10'} />
              : (item.value ?? 0)}
          </span>
        </div>
      ))}
    </div>
  );
};

// ── Bar chart custom tooltip ───────────────────────────────────────────────
const SalesTooltip = ({active, payload, label}) => {
  if (!active || !payload?.length) {return null;}
  return (
    <div
      className={'rounded-md border border-primary-200 px-4 py-3 shadow-lg text-sm'}
      style={{background: '#1e293b'}}
    >
      <p className={'font-bold text-primary-300 mb-1'}>{label}</p>
      <p className={'font-mono text-emerald-400'}>
        ₹{Number(payload[0].value).toLocaleString('en-IN')}
      </p>
      {payload[1] && (
        <p className={'font-mono text-indigo-400'}>
          {payload[1].value} bills
        </p>
      )}
    </div>
  );
};

// ── Yearly summary strip ───────────────────────────────────────────────────
const YearlySummaryStrip = ({summary, loading}) => {
  const items = [
    {label: 'Total Sales',     value: summary?.totalSales      ?? 0, prefix: '₹', color: 'text-emerald-500'},
    {label: 'Total Bills',     value: summary?.totalBills      ?? 0, prefix: '',  color: 'text-indigo-400'},
    {label: 'Items Sold',      value: summary?.totalItemsSold  ?? 0, prefix: '',  color: 'text-sky-400'},
    {label: 'Avg Bill Value',  value: summary?.averageBillValue ?? 0, prefix: '₹', color: 'text-violet-400'},
  ];

  return (
    <div className={'grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5'}>
      {items.map(item => (
        <div
          key={item.label}
          className={'rounded-md border border-primary-100 px-4 py-3 flex flex-col gap-1'}
        >
          <span className={'text-xs font-bold tracking-widest uppercase text-primary-700'}>
            {item.label}
          </span>
          <span className={`text-xl font-bold font-mono ${item.color}`}>
            {loading
              ? <Shimmer className={'h-6 w-24'} />
              : `${item.prefix}${Number(item.value).toLocaleString('en-IN')}`}
          </span>
        </div>
      ))}
    </div>
  );
};

// ── Month labels for X axis ────────────────────────────────────────────────
const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Bar gradient colours — one per month index
const BAR_COLORS = [
  '#6366f1', '#818cf8', '#38bdf8', '#34d399', '#a78bfa',
  '#f472b6', '#fb923c', '#facc15', '#4ade80', '#22d3ee',
  '#c084fc', '#f87171',
];

// ── Main Dashboard ─────────────────────────────────────────────────────────
export const DashboardScreen = () => {
  const auth  = useSelector(selectAuth);
  const token = auth?.tokenData;

  const today = dayjs();

  // ── Date-range filter (KPI cards) ─────────────────────────────────────
  const [filterData, setFilterData] = useState({
    fromDate: today.startOf('day').format('YYYY-MM-DD'),
    toDate:   today.endOf('day').format('YYYY-MM-DD'),
  });

  const {data: stats, isLoading} = RQGetDashboardBills(filterData, token);

  const handleRangeChange = range => {
    if (!range?.[0] || !range?.[1]) {return;}
    setFilterData({
      fromDate: range[0].format('YYYY-MM-DD'),
      toDate:   range[1].format('YYYY-MM-DD'),
    });
  };

  // ── Year selector (bar chart) ─────────────────────────────────────────
  const [selectedYear, setSelectedYear] = useState(today);
  const [yearlyData,   setYearlyData]   = useState(null);

  const {mutate: fetchMonthlySales, isPending: isYearlyLoading} = RQGetMonthlySales(token);

  const loadYearlySales = (year = selectedYear) => {
    fetchMonthlySales(
      {year: year.year()},                                  // ← only year in payload
      {onSuccess: data => setYearlyData(data)},
    );
  };

  useEffect(() => { loadYearlySales(); }, [token]);

  const handleYearChange = year => {
    if (!year) {return;}
    setSelectedYear(year);
    loadYearlySales(year);
  };

  // ── Normalise salesTrend → 12-month array for the bar chart ──────────
  // Expects each item to have a `month` (1-12) and `sales` field.
  // Falls back gracefully if the API returns day-level or differently shaped data.
  const monthIndex = {
    Jan: 1,
    Feb: 2,
    Mar: 3,
    Apr: 4,
    May: 5,
    Jun: 6,
    Jul: 7,
    Aug: 8,
    Sep: 9,
    Oct: 10,
    Nov: 11,
    Dec: 12,
  };

  const barData = (() => {
    const trend = yearlyData?.salesTrend ?? [];

    const map = {};

    trend.forEach(item => {
      const month =
      typeof item.month === 'string'
        ? monthIndex[item.month]
        : item.month;

      if (month) {
        map[month] = item;
      }
    });

    return MONTH_LABELS.map((label, i) => ({
      label,
      sales: map[i + 1]?.sales ?? 0,
      bills: map[i + 1]?.bills ?? 0,
    }));
  })();

  // ── Static card configs ───────────────────────────────────────────────
  const statCards = [
    {
      label: 'Total Sales', value: stats?.totalSales ?? 0, prefix: '₹',
      tag: 'Revenue', tagColor: 'green',
      border: 'border-emerald-500/20', iconBg: 'bg-emerald-600/10', iconColor: 'text-emerald-600',
      icon: <FaSackDollar className={'text-2xl'} />,
    },
    {
      label: 'Total Bills', value: stats?.totalBills ?? 0,
      tag: 'Invoices', tagColor: 'purple',
      border: 'border-indigo-500/20', iconBg: 'bg-indigo-500/10', iconColor: 'text-indigo-600',
      icon: <CgFileDocument className={'text-2xl'} />,
    },
    {
      label: 'Pending Amount', value: stats?.totalPendingAmount ?? 0, prefix: '₹',
      tag: 'Outstanding', tagColor: 'red',
      border: 'border-red-500/20', iconBg: 'bg-red-500/10', iconColor: 'text-red-500',
      icon: <MdPendingActions className={'text-2xl'} />,
    },
  ];

  const gstCards = [
    {
      label: 'GST Sales',     key: 'gstSales',    prefix: '₹',
      iconBg: 'bg-emerald-500/10', iconColor: 'text-emerald-400',
      icon: <HiOutlineCash className={'text-2xl'} />,
    },
    {
      label: 'GST Bills',     key: 'gstBills',    prefix: '',
      iconBg: 'bg-emerald-500/10', iconColor: 'text-emerald-400',
      icon: <FaLayerGroup className={'text-2xl'} />,
    },
    {
      label: 'Non-GST Sales', key: 'nonGstSales', prefix: '₹',
      iconBg: 'bg-pink-500/10', iconColor: 'text-pink-400',
      icon: <RiMoneyRupeeCircleLine className={'text-2xl'} />,
    },
    {
      label: 'Non-GST Bills', key: 'nonGstBills', prefix: '',
      iconBg: 'bg-pink-500/10', iconColor: 'text-pink-400',
      icon: <CiCreditCard1 className={'text-2xl'} />,
    },
  ];

  const panelTitle = text => (
    <span className={'text-xs font-bold tracking-widest uppercase text-primary-900'}>{text}</span>
  );

  const panelStyles = {
    body:   {padding: '16px 20px 20px'},
    header: {borderBottom: '1px solid #b3bcc5', minHeight: 44, paddingInline: 20},
  };

  return (
    <ConfigProvider
      theme={{
        token: {colorPrimary: '#818cf8', borderRadius: 6, fontFamily: 'inherit'},
        components: {
          Card: {
            colorBgContainer:     '#e6e9ec',
            colorBorderSecondary: '#001f3d',
            colorTextHeading:     '#001f3d',
          },
          DatePicker: {
            colorBgContainer:     '#ccd2d8',
            colorBorder:          '#334155',
            colorText:            '#001f3d',
            colorTextPlaceholder: '#001f3d',
            colorIcon:            '#001f3d',
            colorBgElevated:      '#ccd2d8',
          },
          Button: {colorPrimary: '#6366f1', colorPrimaryHover: '#818cf8', borderRadius: 10},
        },
      }}
    >
      <div className={'min-h-screen text-primary-950 px-5 py-7 pb-16'}>

        {/* ── Header ── */}
        <div className={'flex flex-wrap items-center justify-between gap-4 mb-8'}>
          <div>
            <h1 className={'header'}>Sales Dashboard</h1>
            <p className={'text-xs text-primary-600 font-mono mt-1 mb-0'}>billing analytics · real-time</p>
          </div>
          <RangePicker
            defaultValue={[today.startOf('day'), today.endOf('day')]}
            onChange={handleRangeChange}
            allowClear={false}
            format={'DD MMM YYYY'}
          />
        </div>

        {/* ── KPI cards ── */}
        <div className={'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4'}>
          {statCards.map(c => (
            <Card
              key={c.label}
              variant={false}
              className={`rounded-md border ${c.border} shadow-lg hover:-translate-y-1 transition-transform duration-200`}
              styles={{body: {padding: '20px 22px'}}}
            >
              <div className={'flex items-start justify-between mb-4'}>
                <div>
                  <p className={'font-semibold tracking-widest uppercase text-primary-900 mb-1.5'}>
                    {c.label}
                  </p>
                  <Tag color={c.tagColor} className={'text-sm font-semibold tracking-wider border-0 rounded-md'}>
                    {c.tag}
                  </Tag>
                </div>
                <div className={`p-2.5 rounded-md ${c.iconBg} ${c.iconColor}`}>{c.icon}</div>
              </div>
              <div className={'header'}>
                {isLoading
                  ? <Shimmer className={'h-10 w-32'} />
                  : <AnimatedNumber value={c.value} prefix={c.prefix ?? ''} />}
              </div>
            </Card>
          ))}
        </div>

        {/* ── Bill status breakdown ── */}
        <Card
          variant={false}
          title={panelTitle('Bill Status Breakdown')}
          className={'rounded-md border border-primary-100 shadow-md mb-4'}
          styles={panelStyles}
        >
          <BillStatusRow stats={stats} loading={isLoading} />
        </Card>

        {/* ── GST / Non-GST mini cards ── */}
        <div className={'grid grid-cols-2 md:grid-cols-4 gap-4 mb-4'}>
          {gstCards.map(pc => (
            <Card
              key={pc.key}
              variant={false}
              className={'rounded-md border border-primary-100 shadow-md hover:-translate-y-1 transition-transform duration-200'}
              styles={{body: {padding: '18px 20px'}}}
            >
              <div className={'flex items-center gap-3 mb-3'}>
                <div className={`p-2 rounded-lg ${pc.iconBg} ${pc.iconColor}`}>{pc.icon}</div>
                <span className={'font-semibold tracking-widest uppercase text-primary-900 flex-1 text-xs'}>
                  {pc.label}
                </span>
              </div>
              <div className={'header'}>
                {isLoading || !stats
                  ? <Shimmer className={'h-8 w-28'} />
                  : <AnimatedNumber value={stats[pc.key] ?? 0} prefix={pc.prefix} />}
              </div>
            </Card>
          ))}
        </div>

        {/* ── Payment & Top Products ── */}
        <div className={'grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4'}>
          <Card
            variant={false}
            title={panelTitle('Payment Breakdown')}
            className={'rounded-md border border-primary-100 shadow-md'}
            styles={panelStyles}
          >
            {isLoading || !stats
              ? <Shimmer className={'h-28 w-full'} />
              : (
                <PaymentDonut
                  cash={stats.cashSales     ?? 0}
                  upi={stats.upiSales       ?? 0}
                  card={stats.cardSales     ?? 0}
                  credit={stats.creditSales ?? 0}
                />
              )}
          </Card>

          <Card
            variant={false}
            title={panelTitle('Top Products')}
            className={'rounded-md border border-primary-100 shadow-md'}
            styles={panelStyles}
          >
            {isLoading || !stats
              ? <div className={'flex flex-col gap-3'}>{[1, 2, 3, 4].map(i => <Shimmer key={i} className={'h-4 w-full'} />)}</div>
              : <TopProducts products={stats.topProducts ?? []} />}
          </Card>
        </div>

        {/* ── Yearly sales bar chart card ── */}
        <Card
          variant={false}
          title={(
            <div className={'flex items-center justify-between flex-wrap gap-3'}>
              {panelTitle(`Monthly Sales Trend — ${selectedYear.format('YYYY')}`)}

              {/* Year picker — picker="year" gives a year-only selector */}
              <DatePicker
                picker={'year'}
                value={selectedYear}
                onChange={handleYearChange}
                allowClear={false}
                format={'YYYY'}
                disabledDate={d => d && d.isAfter(today, 'year')}
              />
            </div>
          )}
          className={'rounded-md border border-primary-100 shadow-md mb-4'}
          styles={panelStyles}
        >

          {/* Yearly summary strip */}
          <YearlySummaryStrip summary={yearlyData?.summary} loading={isYearlyLoading} />

          {/* Bar chart label */}
          <p className={'text-xs font-bold tracking-widest uppercase text-primary-700 mb-3'}>
            Month-wise Sales ({selectedYear.format('YYYY')})
          </p>

          {/* Bar chart */}
          {isYearlyLoading || !yearlyData
            ? <Shimmer className={'h-56 w-full'} />
            : (
              <ResponsiveContainer width={'100%'} height={240}>
                <BarChart
                  data={barData}
                  margin={{top: 4, right: 8, left: 8, bottom: 0}}
                  barSize={22}
                >
                  <defs>
                    {BAR_COLORS.map((color, i) => (
                      <linearGradient key={i} id={`barGrad${i}`} x1={'0'} y1={'0'} x2={'0'} y2={'1'}>
                        <stop offset={'0%'}   stopColor={color} stopOpacity={0.95} />
                        <stop offset={'100%'} stopColor={color} stopOpacity={0.45} />
                      </linearGradient>
                    ))}
                  </defs>

                  <CartesianGrid strokeDasharray={'3 3'} stroke={'#334155'} vertical={false} />

                  <XAxis
                    dataKey={'label'}
                    tick={{fill: '#64748b', fontSize: 11}}
                    tickLine={false}
                    axisLine={false}
                  />

                  <YAxis
                    tick={{fill: '#64748b', fontSize: 11}}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={v => `₹${(v / 1000).toFixed(0)}k`}
                    width={50}
                  />

                  <Tooltip content={<SalesTooltip />} cursor={{fill: 'rgba(99,102,241,0.08)'}} />

                  <Bar dataKey={'sales'} radius={[5, 5, 0, 0]} maxBarSize={36}>
                    {barData.map((_, i) => (
                      <Cell key={i} fill={`url(#barGrad${i % BAR_COLORS.length})`} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          
          <div className={'grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6'}>
            <div>
              <p className={'text-xs font-bold tracking-widest uppercase text-primary-700 mb-3'}>
                Top Products
              </p>
              {isYearlyLoading || !yearlyData
                ? <div className={'flex flex-col gap-3'}>{[1, 2, 3].map(i => <Shimmer key={i} className={'h-4 w-full'} />)}</div>
                : <TopProducts products={yearlyData.topProducts ?? []} />}
            </div>

            <div>
              <p className={'text-xs font-bold tracking-widest uppercase text-primary-700 mb-3'}>
                Payment Breakdown
              </p>
              {isYearlyLoading || !yearlyData
                ? <Shimmer className={'h-28 w-full'} />
                : (
                  <PaymentDonut
                    cash={yearlyData.paymentBreakdown?.cashSales     ?? 0}
                    upi={yearlyData.paymentBreakdown?.upiSales       ?? 0}
                    card={yearlyData.paymentBreakdown?.cardSales     ?? 0}
                    credit={yearlyData.paymentBreakdown?.creditSales ?? 0}
                  />
                )}
            </div>
          </div>
        </Card>

      </div>
    </ConfigProvider>
  );
};

AnimatedNumber.propTypes      = {value: PropTypes.number, prefix: PropTypes.string, decimals: PropTypes.number};
Shimmer.propTypes             = {className: PropTypes.string};
TopProducts.propTypes         = {products: PropTypes.array};
PaymentDonut.propTypes        = {cash: PropTypes.number, upi: PropTypes.number, card: PropTypes.number, credit: PropTypes.number};
BillStatusRow.propTypes       = {stats: PropTypes.object, loading: PropTypes.bool};
SalesTooltip.propTypes        = {active: PropTypes.bool, payload: PropTypes.array, label: PropTypes.any};
YearlySummaryStrip.propTypes  = {summary: PropTypes.object, loading: PropTypes.bool};

export default DashboardScreen;
